import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { 
  loginAsync, 
  logoutAsync, 
  getUserInfoAsync,
  setPermissions,
  setRoles,
  updateUserPermissions
} from '../redux/slices/authSlice';

/**
 * 认证相关的自定义Hook
 */
export const useAuth = () => {
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);

  const {
    isAuthenticated,
    user,
    token,
    permissions,
    roles,
    loading,
    error
  } = authState;

  // 登录
  const login = useCallback(async (credentials) => {
    try {
      const result = await dispatch(loginAsync(credentials));
      return result.payload;
    } catch (error) {
      throw error;
    }
  }, [dispatch]);

  // 退出登录
  const logout = useCallback(async () => {
    try {
      await dispatch(logoutAsync());
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [dispatch]);

  // 获取用户信息
  const getUserInfo = useCallback(async () => {
    try {
      const result = await dispatch(getUserInfoAsync());
      return result.payload;
    } catch (error) {
      throw error;
    }
  }, [dispatch]);

  // 更新用户权限
  const updatePermissions = useCallback((newPermissions) => {
    dispatch(setPermissions(newPermissions));
  }, [dispatch]);

  // 更新用户角色
  const updateRoles = useCallback((newRoles) => {
    dispatch(setRoles(newRoles));
  }, [dispatch]);

  // 更新用户权限和角色
  const updateUserAuth = useCallback((authData) => {
    dispatch(updateUserPermissions(authData));
  }, [dispatch]);

  // 检查权限
  const hasPermission = useCallback((permissionCode) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(p => p === permissionCode || p.code === permissionCode);
  }, [permissions]);

  // 检查多个权限（任一）
  const hasAnyPermission = useCallback((permissionCodes) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return true;
    return permissionCodes.some(code => hasPermission(code));
  }, [hasPermission]);

  // 检查多个权限（全部）
  const hasAllPermissions = useCallback((permissionCodes) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return true;
    return permissionCodes.every(code => hasPermission(code));
  }, [hasPermission]);

  // 检查角色
  const hasRole = useCallback((roleCode) => {
    if (!roles || roles.length === 0) return false;
    return roles.some(r => r === roleCode || r.code === roleCode || r.roleCode === roleCode);
  }, [roles]);

  // 检查多个角色（任一）
  const hasAnyRole = useCallback((roleCodes) => {
    if (!Array.isArray(roleCodes) || roleCodes.length === 0) return true;
    return roleCodes.some(code => hasRole(code));
  }, [hasRole]);

  // 检查多个角色（全部）
  const hasAllRoles = useCallback((roleCodes) => {
    if (!Array.isArray(roleCodes) || roleCodes.length === 0) return true;
    return roleCodes.every(code => hasRole(code));
  }, [hasRole]);

  // 检查是否为管理员
  const isAdmin = useMemo(() => {
    return hasRole('PLATFORM_ADMIN') || hasRole('SUPER_ADMIN');
  }, [hasRole]);

  // 检查是否为超级管理员
  const isSuperAdmin = useMemo(() => {
    return hasRole('SUPER_ADMIN');
  }, [hasRole]);

  // 获取用户类型
  const userType = useMemo(() => {
    return user?.userType || 'PERSONAL';
  }, [user]);

  // 检查是否为企业用户
  const isEnterpriseUser = useMemo(() => {
    return userType === 'ENTERPRISE';
  }, [userType]);

  // 检查是否为个人用户
  const isPersonalUser = useMemo(() => {
    return userType === 'PERSONAL';
  }, [userType]);

  return {
    // 状态
    isAuthenticated,
    user,
    token,
    permissions,
    roles,
    loading,
    error,
    
    // 用户类型
    userType,
    isEnterpriseUser,
    isPersonalUser,
    isAdmin,
    isSuperAdmin,
    
    // 方法
    login,
    logout,
    getUserInfo,
    updatePermissions,
    updateRoles,
    updateUserAuth,
    
    // 权限检查
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  };
};
